import { useState } from 'react'
import type { Navigate } from '../App'
import type { Block, Island } from '../types'
import { useIsland, useStore } from '../state/store'
import { uid } from '../lib/id'
import { parsePinyin, prettyPinyin } from '../lib/pinyin'
import { ToneReference } from '../components/ToneRibbon'
import { SpeakButton } from '../components/SpeakButton'
import { languageOf } from '../lib/languages'

const hanCount = (s: string) => (s.match(/[一-鿿]/gu) ?? []).length

/**
 * Live check on a block's transcription.
 *
 * One Han character is one syllable, so a count mismatch means the hanzi and
 * the pinyin disagree — and since the pinyin grader scores against that field,
 * a silent mismatch would mark correct answers wrong forever. Better to say so
 * while it is being typed.
 */
function blockWarning(block: Block): string | null {
  if (!block.hanzi.trim() || !block.pinyin.trim()) return null

  const parsed = parsePinyin(block.pinyin)
  const unreadable = parsed.filter((s) => !s.base).map((s) => s.raw)
  if (unreadable.length) {
    return `Can't read "${unreadable.join('", "')}" as pinyin. Check the spelling.`
  }

  const chars = hanCount(block.hanzi)
  if (chars !== parsed.length) {
    return `${chars} characters but ${parsed.length} syllables. Pinyin drills will mark this wrong.`
  }
  return null
}

export function IslandEditor({
  id,
  draft: initial,
  navigate,
}: {
  id: string
  /**
   * An island that does not exist in the store yet, from "Build an island".
   *
   * Present only for a brand-new island. It is what makes Cancel mean cancel:
   * nothing has been written, so leaving writes nothing either.
   */
  draft?: Island
  navigate: Navigate
}) {
  const stored = useIsland(id)
  const { dispatch } = useStore()
  const [draft, setDraft] = useState<Island | null>(stored ? { ...stored } : (initial ?? null))
  const [problem, setProblem] = useState<string | null>(null)

  // Only a real absence — an id with neither a saved island nor a new draft
  // behind it, which means a stale link or an island deleted on another device.
  if (!draft) {
    return (
      <div className="empty">
        That island is gone.{' '}
        <button type="button" className="btn is-small" onClick={() => navigate({ view: 'islands' })}>
          Back to islands
        </button>
      </div>
    )
  }

  const lang = languageOf(draft.language)

  const patch = (fields: Partial<Island>) => setDraft({ ...draft, ...fields })

  const patchBlock = (blockId: string, fields: Partial<Block>) =>
    patch({
      blocks: draft.blocks.map((b) => (b.id === blockId ? { ...b, ...fields } : b)),
    })

  const save = () => {
    /*
     * An island with no block in it cannot be drilled, performed, or pointed at
     * by a scenario — saving one produces a row in the list that does nothing.
     * That is the same empty island Cancel used to leave behind, reached by the
     * other button, so it is refused here rather than created and regretted.
     */
    if (!draft.blocks.some((b) => b.hanzi.trim() || b.pinyin.trim())) {
      setProblem('Write at least one block first — an island with nothing in it cannot be drilled.')
      return
    }

    dispatch({
      type: 'upsertIsland',
      island: {
        ...draft,
        // Once you have rewritten it, it is yours — the starter nag goes away.
        isStarter: false,
        updatedAt: Date.now(),
        blocks: draft.blocks.filter((b) => b.hanzi.trim() || b.pinyin.trim()),
      },
    })
    navigate({ view: 'island', id: draft.id })
  }

  return (
    <>
      {/* Back to wherever leaving actually returns you to. A new island has no
          detail page to go back to — going there was what showed an empty
          block — so cancelling out of one returns to the list it started from. */}
      <button
        type="button"
        className="btn is-quiet is-small"
        onClick={() => navigate(stored ? { view: 'island', id } : { view: 'islands' })}
        style={{ marginBottom: '0.8rem' }}
      >
        ← Cancel
      </button>

      <header className="page-head">
        <div className="eyebrow">Editing</div>
        <h1 className="page-title">Shape the island</h1>
        <p className="lede">
          Write blocks you would actually say. Keep each one to a single breath — if you cannot
          deliver it without a pause, it is two blocks.
        </p>
      </header>

      <div className="card">
        <div className="grid grid-2">
          <label className="field">
            <span className="field-label">English name</span>
            <input
              className="input"
              value={draft.title}
              onChange={(e) => patch({ title: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field-label">Chinese name</span>
            <input
              className="input is-han"
              value={draft.titleZh}
              onChange={(e) => patch({ titleZh: e.target.value })}
            />
          </label>
        </div>

        <label className="field">
          <span className="field-label">When you'd reach for it</span>
          <input
            className="input"
            value={draft.useWhen}
            placeholder="The moment in a conversation this island answers"
            onChange={(e) => patch({ useWhen: e.target.value })}
          />
        </label>

        <label className="field" style={{ maxWidth: '8rem' }}>
          <span className="field-label">HSK level</span>
          <select
            className="select"
            value={draft.hskLevel}
            onChange={(e) => patch({ hskLevel: Number(e.target.value) })}
          >
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                HSK {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="divider" />

      <div className="stack">
        {draft.blocks.map((block, i) => {
          const warning = blockWarning(block)
          return (
            <div key={block.id} className="card">
              <div className="card-head">
                {/* Grouped so card-head's space-between puts Remove on the
                    right and leaves the audio beside the block number. */}
                <div className="row" style={{ gap: '0.6rem', alignItems: 'center' }}>
                  <span className="block-index">{String(i + 1).padStart(2, '0')}</span>
                  {block.hanzi.trim() && (
                    <SpeakButton
                      text={block.hanzi}
                      language={lang.code}
                      withSlow
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="btn is-small is-quiet is-danger"
                  onClick={() => patch({ blocks: draft.blocks.filter((b) => b.id !== block.id) })}
                >
                  Remove
                </button>
              </div>

              {/* Labelled from the language registry rather than hard-coded.
                  This said "Characters" and offered a pinyin box in every
                  language — so editing a German island asked for pinyin, with a
                  Mandarin example underneath it. */}
              <label className="field">
                <span className="field-label">{lang.targetLabel}</span>
                <textarea
                  className={`textarea ${lang.code === 'zh' ? 'is-han' : ''}`}
                  rows={2}
                  value={block.hanzi}
                  onChange={(e) => patchBlock(block.id, { hanzi: e.target.value })}
                />
              </label>

              {lang.hasPhonetics && (
                <label className="field">
                  <span className="field-label">
                    {lang.phoneticLabel ?? 'Phonetics'} — space-separated syllables
                  </span>
                  <input
                    className="input is-data"
                    value={block.pinyin}
                    placeholder="wo3 shi4 ruan3 jian4 — tone numbers become marks"
                    onChange={(e) => patchBlock(block.id, { pinyin: e.target.value })}
                    onBlur={(e) => patchBlock(block.id, { pinyin: prettyPinyin(e.target.value) })}
                  />
                </label>
              )}

              <label className="field">
                <span className="field-label">English</span>
                <input
                  className="input"
                  value={block.english}
                  onChange={(e) => patchBlock(block.id, { english: e.target.value })}
                />
              </label>

              <label className="field">
                <span className="field-label">Note (optional)</span>
                <input
                  className="input"
                  value={block.note ?? ''}
                  placeholder="A pattern worth stealing, a trap to avoid"
                  onChange={(e) => patchBlock(block.id, { note: e.target.value })}
                />
              </label>

              {warning ? (
                <div className="notice is-error">{warning}</div>
              ) : (
                lang.hasPhonetics && <ToneReference pinyin={block.pinyin} />
              )}
            </div>
          )
        })}
      </div>

      <div className="row" style={{ marginTop: '1rem' }}>
        <button
          type="button"
          className="btn"
          onClick={() =>
            patch({
              blocks: [...draft.blocks, { id: uid('blk'), hanzi: '', pinyin: '', english: '' }],
            })
          }
        >
          Add block
        </button>
        <button type="button" className="btn is-primary" onClick={save}>
          Save island
        </button>
      </div>

      {problem && (
        <div className="notice is-error" style={{ marginTop: '0.8rem' }}>
          {problem}
        </div>
      )}
    </>
  )
}
